// Library Methods
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { useTheme } from "@mui/material/styles";
import { CancelIcon, SearchIcon } from "src/assets/icons";

// Styles
import {
  CustomIconButtonDark,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";
import { UI_SIZES } from "src/constants/uiTokens";

type ExpandableSearchbarType = {
  id?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  searchTooltip?: string;
  openByDefault?: boolean;
  autofocusByDefault?: boolean;
  searchTerm: string;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onClear: () => void;
};

const ExpandableSearchbar = ({
  id = undefined,
  placeholder = undefined,
  isDisabled = false,
  isLoading = false,
  openByDefault = false,
  autofocusByDefault = undefined,
  searchTooltip = "",
  searchTerm,
  onChange,
  onClear,
}: ExpandableSearchbarType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, inputBorder } = getSurfaceTokens(theme);

  const [expandSearchbar, setExpandSearchbar] = useState(openByDefault);
  // Opened by the user clicking the search button, rather than by default
  const [isOpenedByButton, setIsOpenedByButton] = useState(false);

  // if there are multiple expanded searchbars on the same page
  // we don't always want the last input to be autofocused
  const autofocusDefault =
    openByDefault && autofocusByDefault === undefined
      ? true
      : Boolean(openByDefault && autofocusByDefault);

  // the user asked to search, so they always want to type straight away
  const autofocus = isOpenedByButton || autofocusDefault;

  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        height={UI_SIZES.inputHeight}
        width={expandSearchbar ? 250 : UI_SIZES.iconButtonSize}
        sx={{
          borderRadius: "4px",
        }}
      />
    );
  }

  return (
    <>
      {expandSearchbar ? (
        <TextField
          id={id}
          autoFocus={autofocus}
          placeholder={placeholder ?? t("Search...")}
          value={searchTerm}
          variant="standard"
          autoComplete="off"
          inputProps={{
            sx: {
              fontSize: "14px",
              "&::placeholder": {
                color: inputBorder,
                opacity: 1,
              },
              // If the browser autofills anyway, use our custom colors
              "&:-webkit-autofill": {
                WebkitTextFillColor: onSurface,
                transition: "background-color 600000s 0s",
              },
            },
          }}
          InputProps={{
            disableUnderline: true,
            style: {
              backgroundColor: "#5B6D7A10",
              color: onSurface,
              height: `${UI_SIZES.inputHeight}px`,
              border: "1px solid #5B6D7A",
              borderRadius: "4px",
            },
            startAdornment: (
              <Box sx={{ display: "flex", alignItems: "center", mx: "8px" }}>
                <SearchIcon />
              </Box>
            ),
            endAdornment: (
              <Box
                data-testid="reset-searchbar-button"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mx: "8px",
                  cursor: "pointer",
                }}
                onClick={() => {
                  onClear();
                  setExpandSearchbar(false);
                  setIsOpenedByButton(false);
                }}
              >
                <CancelIcon />
              </Box>
            ),
          }}
          data-testid="expandable-searchbar"
          onChange={onChange}
          sx={{
            width: { sm: 200, md: 150, lg: 250 },
            mb: 0,
          }}
        />
      ) : (
        <Tooltip title={searchTooltip}>
          <IconButton
            data-testid="expandable-search-icon"
            aria-label="search"
            sx={CustomIconButtonDark}
            disabled={isDisabled}
            onClick={() => {
              if (expandSearchbar) onClear();
              setExpandSearchbar((prev) => !prev);
              setIsOpenedByButton(true);
            }}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

export default ExpandableSearchbar;
